import React, { useEffect, useMemo } from "react";
import { useForm } from "../../Components/Form/useForm";
import Controls from "../../Components/Form/Controls";
import { CustomDropdown, TextInput, TelInput, SelectableCard } from "../../Components/atoms";
import { Stepper } from "../shared";
import {
  FaShieldAlt,
  FaUser,
  FaPhone,
  FaCreditCard,
  FaCogs,
  FaSave,
  FaWarehouse,
  FaHandshake,
  FaUserTie,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import { FaEnvelope } from "react-icons/fa";
import { useLazyGetRoleTemplatesQuery, useLazyGetDealerRolesRoleTemplatesQuery, useLazySearchFirmsForDropdownQuery } from "../../store";
import useAuth from "../../Hooks/useAuth";
import usePermission from "../../Hooks/usePermission";

const labelClass = "block text-sm font-medium text-gray-700 mb-2";

const isValidEmail = (email) => {
  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
  return emailPattern.test(email);
};

const createDefaultAutoReportMailConfig = () => ({
  daily: { enabled: false, sendTime: "08:00" },
  weekly: { enabled: false, sendTime: "08:00", daysOfWeek: [] },
  monthly: { enabled: false, sendTime: "08:00", dayOfMonth: 1 },
});

const normalizeAutoReportMailConfig = (config) => {
  const base = createDefaultAutoReportMailConfig();
  const allowedDays = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  return {
    daily: {
      enabled: config?.daily?.enabled ?? base.daily.enabled,
      sendTime: config?.daily?.sendTime || base.daily.sendTime,
    },
    weekly: {
      enabled: config?.weekly?.enabled ?? base.weekly.enabled,
      sendTime: config?.weekly?.sendTime || base.weekly.sendTime,
      daysOfWeek: Array.isArray(config?.weekly?.daysOfWeek)
        ? config.weekly.daysOfWeek.filter((day) => allowedDays.includes(day))
        : base.weekly.daysOfWeek,
    },
    monthly: {
      enabled: config?.monthly?.enabled ?? base.monthly.enabled,
      sendTime: config?.monthly?.sendTime || base.monthly.sendTime,
      dayOfMonth:
        typeof config?.monthly?.dayOfMonth === "number"
          ? Math.min(Math.max(config.monthly.dayOfMonth, 1), 31)
          : base.monthly.dayOfMonth,
    },
  };
};

const WEEK_DAYS = [
  { id: "mon", label: "Pzt" },
  { id: "tue", label: "Sal" },
  { id: "wed", label: "Çar" },
  { id: "thu", label: "Per" },
  { id: "fri", label: "Cum" },
  { id: "sat", label: "Cmt" },
  { id: "sun", label: "Paz" },
];

const SectionHeader = ({ icon: Icon, iconBg, title }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className={`flex items-center justify-center w-8 h-8 rounded-xl ${iconBg}`}>
      <Icon className="w-4 h-4" />
    </div>
    <h3 className="text-base font-semibold text-gray-800">{title}</h3>
  </div>
);

const FormCard = ({ children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5">
    {children}
  </div>
);

/** Ana sistem rolleri */
const ALL_ROLE_OPTIONS = [
  { id: "storage", title: "Depo" },
  { id: "dealer", title: "Bayi" },
  { id: "customer", title: "Müşteri" },
  { id: "machineworker", title: "Makine İşçisi" },
];

/** Rol → oluşturma yetkisi eşleşmesi */
const ROLE_CREATE_PERMISSION_MAP = {
  storage: "users.createStorage",
  dealer: "users.createDealer",
  customer: "users.createCustomer",
  machineworker: "users.createMachineworker",
};

/** Firma tipine göre izin verilen ana roller */
const ROLES_BY_FIRM_TYPE = {
  customer: [{ id: "customer", title: "Müşteri" }],
  dealer: [{ id: "dealer", title: "Bayi" }],
  storage: [
    { id: "storage", title: "Depo" },
    { id: "machineworker", title: "Makine İşçisi" },
  ],
};

/** SubRole gerektiren roller */
const ROLES_WITH_SUBROLE = ["storage", "dealer"];

/** Rol adını Türkçe göstermek için */
const ROLE_DISPLAY_NAME = {
  storage: "Depo",
  dealer: "Bayi",
  customer: "Müşteri",
  machineworker: "Makine İşçisi",
};

const SUBROLE_DISPLAY_NAME = {
  management: "Yönetim",
  service: "Servis",
  playmaker: "Plasiyer",
  accounting: "Muhasebe",
  depo: "Depo", // bayi (dealer) subRole'ü — storage rolünden ayrı, isim çakışması kasıtlı değil
  manufacturer: "Üretim",
};

/** Firma tipi kartları için ikon + kısa açıklama */
const FIRM_TYPE_META = {
  storage: { icon: FaWarehouse, subtitle: "Depo & yönetim rolleri" },
  dealer: { icon: FaHandshake, subtitle: "Bayi ağı yönetimi" },
  customer: { icon: FaUserTie, subtitle: "Müşteri firması kullanıcısı" },
  machineworker: { icon: FaCogs, subtitle: "Saha makine operatörü" },
};

/** Sihirbaz adım etiketleri (yalnızca oluşturma modu) */
const WIZARD_STEPS = ["Firma Tipi", "Firma & Rol", "Bilgiler"];
const TOTAL_STEPS = WIZARD_STEPS.length;

export default function UserForm({
  handleOpenModal,
  token,
  initialFValues,
  isCreate,
  /** Firma tipi: 'customer' | 'dealer' | 'storage' — seçili firmaya göre roller filtrelenir */
  firmTypeFromFirm,
  /** Firma kullanıcılar sekmesinden açıldığında firma sabit; dropdown yerine metin gösterilir */
  fixedFirmName,
  /** Firma ID — firma sekmesinden açıldığında doğrudan geçilir (template fetch için) */
  fixedFirmId,
  /** Drawer kapatma / iptal (footer İptal butonu) */
  onCancel,
}) {
  const { auth } = useAuth();
  const { hasPermission: hasCreatePerm } = usePermission();

  // Firma arama — debounce search API
  const [searchFirms, { data: firmSearchData, isFetching: isFirmSearching }] =
    useLazySearchFirmsForDropdownQuery();

  // Rol şablonu desteği — auth role'e göre doğru endpoint'i kullan
  const isDealer = auth.role === "dealer";
  const [fetchStorageTemplates, { data: storageTemplatesData }] = useLazyGetRoleTemplatesQuery();
  const [fetchDealerTemplates, { data: dealerTemplatesData }] = useLazyGetDealerRolesRoleTemplatesQuery();
  const templatesData = isDealer ? dealerTemplatesData : storageTemplatesData;
  const fetchTemplates = isDealer ? fetchDealerTemplates : fetchStorageTemplates;
  const roleTemplates = templatesData?.data?.templates || [];
  // Edit modunda kullanıcının mevcut şablonu dropdown'da seçili gelir
  const [selectedTemplateId, setSelectedTemplateId] = React.useState(
    initialFValues?.roleTemplate ? String(initialFValues.roleTemplate) : ""
  );

  // Sihirbaz adımı — yalnızca oluşturma modunda (isCreate) kullanılır.
  const wizard = !!isCreate;
  const [step, setStep] = React.useState(1);

  const roleOptions = useMemo(() => {
    const base = firmTypeFromFirm && ROLES_BY_FIRM_TYPE[firmTypeFromFirm]
      ? ROLES_BY_FIRM_TYPE[firmTypeFromFirm]
      : ALL_ROLE_OPTIONS;
    return base.filter((opt) => {
      const perm = ROLE_CREATE_PERMISSION_MAP[opt.id];
      return !perm || hasCreatePerm(perm);
    });
  }, [firmTypeFromFirm, hasCreatePerm]);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    if ("role" in fieldValues)
      temp.role = fieldValues.role ? "" : "Kullanıcı rolü seçilmedi.";
    if ("firmName" in fieldValues && fieldValues.role !== "machineworker")
      temp.firmName = fieldValues.firmName ? "" : "Firma seçilmedi.";
    if ("email" in fieldValues)
      temp.email = isValidEmail(fieldValues.email)
        ? ""
        : "Doğru bir email adresi girin.";
    if ("name" in fieldValues)
      temp.name = fieldValues.name ? "" : "İsim girin.";
    if ("lastName" in fieldValues)
      temp.lastName = fieldValues.lastName ? "" : "Soyisim girin.";
    if ("tel" in fieldValues)
      temp.tel = fieldValues.tel ? "" : "Telefon girin.";
    if (
      "subRole" in fieldValues &&
      ROLES_WITH_SUBROLE.includes(fieldValues.role)
    )
      temp.subRole = fieldValues.subRole ? "" : "Alt rol seçilmedi.";

    setErrors({
      ...temp,
    });

    if (fieldValues === values)
      return Object.values(temp).every((x) => x === "");
  };

  const normalizedInitialValues = useMemo(
    () => ({
      ...initialFValues,
      autoReportMailConfig: normalizeAutoReportMailConfig(
        initialFValues?.autoReportMailConfig
      ),
    }),
    [initialFValues]
  );

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    normalizedInitialValues,
    true,
    validate
  );

  /** Rol → firmType eşleşmesi */
  const firmTypeForRole = useMemo(() => {
    const role = values.role;
    if (role === "storage" || role === "machineworker") return "storage";
    if (role === "dealer") return "dealer";
    if (role === "customer") return "customer";
    return null;
  }, [values.role]);

  /** Firma arama callback — CustomDropdown onSearch'e verilir */
  const handleFirmSearch = React.useCallback(
    (search) => {
      if (!token || !firmTypeForRole) return;
      searchFirms({ token, search, firmType: firmTypeForRole });
    },
    [token, firmTypeForRole, searchFirms]
  );

  // Rol seçildiğinde başlangıç firma listesini çek
  useEffect(() => {
    if (firmTypeForRole && token) {
      searchFirms({ token, search: "", firmType: firmTypeForRole });
    }
  }, [firmTypeForRole, token, searchFirms]);

  /** API'den dönen firma listesi */
  const firmOptions = useMemo(() => {
    return (firmSearchData?.data?.firms || []).map((f) => ({
      id: f._id,
      title: f.firmName,
      firmType: f.firmType,
    }));
  }, [firmSearchData]);

  const reportConfig =
    values.autoReportMailConfig || createDefaultAutoReportMailConfig();

  // Rol değişince firma ve subRole sıfırla. Firma sabitse (firma detayından
  // açıldıysa) firma korunur — aksi halde validate() gizli firma alanında
  // "Firma seçilmedi" verir ve son adımda Önizle sessizce hiçbir şey yapmaz.
  const prevRoleRef = React.useRef(values.role);
  useEffect(() => {
    if (prevRoleRef.current !== values.role) {
      prevRoleRef.current = values.role;
      setValues((prev) => ({ ...prev, firmName: fixedFirmName || "", subRole: "" }));
      setSelectedTemplateId("");
    }
  }, [values.role, setValues, fixedFirmName]);

  // Firma seçildiğinde rol şablonlarını çek
  // Firma değişince şablon seçimi sıfırlanır; mount'ta (edit prefill) sıfırlanmaz.
  const prevFirmKeyRef = React.useRef(fixedFirmId || initialFValues?.firmName || "");
  useEffect(() => {
    const selectedFirm = firmOptions.find((f) => f.title === values.firmName);
    const firmId = fixedFirmId || selectedFirm?.id;
    if (firmId && token) {
      fetchTemplates({ token, id: firmId });
    }
    const firmKey = fixedFirmId || values.firmName || "";
    if (prevFirmKeyRef.current !== firmKey) {
      prevFirmKeyRef.current = firmKey;
      setSelectedTemplateId("");
    }
  }, [values.firmName, fixedFirmId, token, fetchTemplates, firmOptions]);

  // Şablon seçildiğinde subRole'ü doldur
  useEffect(() => {
    if (!selectedTemplateId) return;
    const template = roleTemplates.find((t) => String(t._id) === selectedTemplateId);
    if (template) {
      setValues((prev) => ({ ...prev, subRole: template.subRole }));
    }
  }, [selectedTemplateId, roleTemplates, setValues]);

  // Adım bazlı geçerlilik — İleri butonunu aktif/pasif yapar.
  const stepIsValid = (n) => {
    if (n === 1) return !!values.role;
    if (n === 2) {
      const firmOk =
        values.role === "machineworker" || !!values.firmName || !!fixedFirmName;
      const subRoleOk =
        !ROLES_WITH_SUBROLE.includes(values.role) || !!values.subRole;
      return firmOk && subRoleOk;
    }
    if (n === 3)
      return (
        !!values.name &&
        !!values.lastName &&
        isValidEmail(values.email) &&
        !!values.tel
      );
    return true;
  };

  const goNext = () => {
    if (stepIsValid(step)) setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 1));
  // Wizard'da yalnızca aktif adımı göster; edit modunda hepsi görünür.
  const showStep = (n) => !wizard || step === n;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Wizard'da son adıma gelmeden submit = bir sonraki adıma geç (Enter dahil).
    if (wizard && step < TOTAL_STEPS) {
      goNext();
      return;
    }
    if (validate()) {
      const submitValues = { ...values };
      // Secili sablon varsa sablon referansini ekle (roleTemplate referansi
      // sonraki sablon guncellemelerinde cascade'in bu kullaniciyi bulabilmesi
      // icin gerekli). Edit modunda permissions client'tan gonderilmez —
      // server sablon degisikligini gorunce subRole + permissions'i sablondan uygular.
      if (selectedTemplateId) {
        submitValues.roleTemplate = selectedTemplateId;
        if (isCreate) {
          const template = roleTemplates.find((t) => String(t._id) === selectedTemplateId);
          if (template?.permissions?.length) {
            submitValues.templatePermissions = template.permissions;
          }
        }
      }
      handleOpenModal(submitValues);
    }
  };

  const selectedRoleOption = roleOptions.find((o) => o.id === values.role);
  const roleDropdownValue = selectedRoleOption
    ? { id: selectedRoleOption.id, label: selectedRoleOption.title }
    : null;
  const selectedFirmOption = firmOptions.find((o) => o.title === values.firmName);
  const firmDropdownValue = selectedFirmOption
    ? { id: selectedFirmOption.id, label: selectedFirmOption.title }
    : null;

  // Firma alanı — hem wizard 2. adımda hem edit modunda kullanılır.
  const renderFirmField = () =>
    values.role === "machineworker" ? null : fixedFirmName ? (
      <div>
        <label className={labelClass}>Firma</label>
        <p className="text-gray-900 font-medium bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
          {fixedFirmName}
        </p>
      </div>
    ) : (
      <CustomDropdown
        label="Firma"
        placeholder={values.role ? "Firma ara ve seçin..." : "Önce firma tipi seçin"}
        searchPlaceholder="Firma adı yazın..."
        options={firmOptions.map((o) => ({ id: o.id, label: o.title }))}
        value={firmDropdownValue}
        onChange={(option) =>
          handleInputChange({ target: { name: "firmName", value: option?.label ?? "" } })
        }
        onSearch={handleFirmSearch}
        loading={isFirmSearching}
        error={!!errors.firmName}
        errorMessage={errors.firmName}
        disabled={!values.role}
        showSearch={true}
      />
    );

  // Rol Şablonu kartı — subRole gerektiren roller için.
  // Şablon adı = alt rol; alt rol etiketi addan farklıysa (eski şablon) parantezde.
  const templateLabel = (t) => {
    const sub = SUBROLE_DISPLAY_NAME[t.subRole] || t.subRole;
    return !sub || sub === t.name || t.subRole === t.name ? t.name : `${t.name} (${sub})`;
  };

  const renderRoleTemplateCard = () => (
    <FormCard>
      <SectionHeader icon={FaCogs} iconBg="bg-blue-100 text-blue-600" title="Rol Şablonu" />
      {roleTemplates.length > 0 ? (
        <>
          <CustomDropdown
            label="Rol Şablonu"
            placeholder="Şablon seçin"
            searchPlaceholder="Şablon ara..."
            options={roleTemplates.map((t) => ({
              id: t._id,
              label: templateLabel(t),
            }))}
            value={
              selectedTemplateId
                ? (() => {
                    const t = roleTemplates.find((t) => String(t._id) === selectedTemplateId);
                    return t
                      ? { id: t._id, label: templateLabel(t) }
                      : null;
                  })()
                : null
            }
            onChange={(option) => setSelectedTemplateId(option?.id ?? "")}
            error={!!errors.subRole}
            errorMessage={errors.subRole}
            showSearch={true}
          />
          <p className="text-xs text-gray-400 mt-1">
            Şablon seçildiğinde alt rol ve yetkiler otomatik atanır.
          </p>
        </>
      ) : (
        <p className="text-sm text-gray-500">Bu firma için tanımlı rol şablonu bulunamadı.</p>
      )}
    </FormCard>
  );

  // machineworker'a şablon gösterilmez — şemasında subRole/roleTemplate/permissions
  // alanı yok; kapsamı yalnız atanan cihazlardır (#389).
  const showRoleTemplate =
    ROLES_WITH_SUBROLE.includes(values.role) && (values.firmName || fixedFirmName);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      {wizard && (
        <div className="flex-none px-2 pt-1 pb-5">
          <Stepper steps={WIZARD_STEPS} current={step} />
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-4">
        {/* ADIM 1 — Firma Tipi */}
        {showStep(1) && (
          <FormCard>
            <SectionHeader
              icon={FaShieldAlt}
              iconBg="bg-indigo-100 text-indigo-600"
              title={wizard ? "Firma Tipi" : "Firma Tipi ve Firma"}
            />
            {wizard ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {roleOptions.map((o) => {
                    const meta = FIRM_TYPE_META[o.id] || {};
                    return (
                      <SelectableCard
                        key={o.id}
                        icon={meta.icon}
                        title={o.title}
                        subtitle={meta.subtitle}
                        selected={values.role === o.id}
                        onClick={() =>
                          handleInputChange({ target: { name: "role", value: o.id } })
                        }
                      />
                    );
                  })}
                </div>
                {errors.role && <p className="mt-2 text-sm text-red-500">{errors.role}</p>}
              </>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CustomDropdown
                  label="Firma Tipi"
                  placeholder="Firma tipi seçin"
                  searchPlaceholder="Ara..."
                  options={roleOptions.map((o) => ({ id: o.id, label: o.title }))}
                  value={roleDropdownValue}
                  onChange={(option) =>
                    handleInputChange({ target: { name: "role", value: option?.id ?? "" } })
                  }
                  error={!!errors.role}
                  errorMessage={errors.role}
                  disabled={!isCreate}
                  showSearch={true}
                />
                {renderFirmField()}
              </div>
            )}
          </FormCard>
        )}

        {/* ADIM 2 (wizard) — Firma */}
        {wizard && showStep(2) && values.role !== "machineworker" && (
          <FormCard>
            <SectionHeader icon={FaShieldAlt} iconBg="bg-blue-100 text-blue-600" title="Firma" />
            {renderFirmField()}
          </FormCard>
        )}

        {/* Rol Şablonu — edit'te her zaman, wizard'da 2. adımda */}
        {showStep(2) && showRoleTemplate && renderRoleTemplateCard()}

        {/* ADIM 3 — Kişisel Bilgiler */}
        {showStep(3) && (
        <FormCard>
          <SectionHeader icon={FaUser} iconBg="bg-emerald-100 text-emerald-600" title="Kişisel Bilgiler" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextInput
              name="name"
              label="İsim"
              value={values.name || ""}
              onChange={handleInputChange}
              placeholder="İsim"
              error={!!errors.name}
              errorMessage={errors.name}
            />
            <TextInput
              name="lastName"
              label="Soyisim"
              value={values.lastName || ""}
              onChange={handleInputChange}
              placeholder="Soyisim"
              error={!!errors.lastName}
              errorMessage={errors.lastName}
            />
          </div>
        </FormCard>
        )}

        {/* İletişim Bilgileri — iki sütun */}
        {showStep(3) && (
        <FormCard>
          <SectionHeader icon={FaPhone} iconBg="bg-amber-100 text-amber-600" title="İletişim Bilgileri" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextInput
              name="email"
              label="Email"
              type="email"
              value={values.email || ""}
              onChange={handleInputChange}
              placeholder="Email"
              error={!!errors.email}
              errorMessage={errors.email}
              disabled={!isCreate}
            />
            <TelInput
              name="tel"
              label="Telefon"
              value={values.tel}
              onChange={handleInputChange}
              error={!!errors.tel}
              errorMessage={errors.tel}
            />
          </div>
        </FormCard>
        )}

        {/* Fabrika NFC Kartı - tüm storage kullanıcıları (opsiyonel, D-032) */}
        {showStep(3) && values.role === "storage" && (
          <FormCard>
            <SectionHeader
              icon={FaCreditCard}
              iconBg="bg-amber-100 text-amber-600"
              title="Fabrika NFC Kartı"
            />
            <div>
              <label className={labelClass}>
                Kart UID <span className="text-gray-400 font-normal">(opsiyonel)</span>
              </label>
              <Controls.NFCInput
                name="nfcCardId"
                value={values.nfcCardId}
                onChange={handleInputChange}
                error={errors.nfcCardId}
                label=""
              />
              <p className="mt-1 text-xs text-gray-400">
                USB NFC okuyucuyla kartı okutun. Eşlenen kartla çalışan fabrika
                tabletlerinde tanınır; boş bırakılırsa fabrika akışlarını kullanamaz.
                Kart değişikliği geçmişi sistemde tutulur.
              </p>
            </div>
          </FormCard>
        )}

        {/* Kart Bilgileri - Servis subRole */}
        {showStep(3) && values.role === "storage" && values.subRole === "service" && (
          <FormCard>
            <SectionHeader icon={FaCreditCard} iconBg="bg-purple-100 text-purple-600" title="Kart Bilgileri" />
            <div>
              <label className={labelClass}>Kart ID</label>
              <Controls.NFCInput
                name="cardId"
                value={values.cardId}
                onChange={handleInputChange}
                error={errors.cardId}
                label=""
              />
              {errors.cardId && <p className="mt-1 text-sm text-red-500">{errors.cardId}</p>}
            </div>
          </FormCard>
        )}

        {/* Otomatik Rapor Mail Ayarları - Müşteri */}
        {showStep(3) && values.role === "customer" && (
          <FormCard>
            <SectionHeader icon={FaEnvelope} iconBg="bg-green-100 text-green-600" title="Otomatik Rapor Mail Ayarları" />
            <div className="grid grid-cols-1 gap-4">
              {[
                { key: "daily", label: "Günlük Rapor" },
                { key: "weekly", label: "Haftalık Rapor" },
                { key: "monthly", label: "Aylık Rapor" },
              ].map(({ key, label }) => (
                <div key={key} className={`rounded-xl p-3 sm:p-4 border transition-colors ${reportConfig[key]?.enabled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
                  <label className="flex items-center justify-between text-gray-700 font-medium text-sm mb-2">
                    <span>{label}</span>
                    <input
                      type="checkbox"
                      checked={reportConfig[key]?.enabled || false}
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        setValues({
                          ...values,
                          autoReportMailConfig: {
                            ...reportConfig,
                            [key]: { ...reportConfig[key], enabled },
                          },
                        });
                      }}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2"
                    />
                  </label>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Gönderim Saati</span>
                      <input
                        type="time"
                        value={reportConfig[key]?.sendTime || "08:00"}
                        onChange={(e) => {
                          const sendTime = e.target.value || "08:00";
                          setValues({
                            ...values,
                            autoReportMailConfig: {
                              ...reportConfig,
                              [key]: { ...reportConfig[key], sendTime },
                            },
                          });
                        }}
                        disabled={!reportConfig[key]?.enabled}
                        className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none disabled:bg-gray-100 mt-0.5"
                      />
                    </div>

                    {key === "weekly" && (
                      <div>
                        <span className="text-xs text-gray-500">Gün Seçimi</span>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {WEEK_DAYS.map((day) => {
                            const isChecked = reportConfig.weekly?.daysOfWeek?.includes(day.id) || false;
                            return (
                              <button
                                key={day.id}
                                type="button"
                                disabled={!reportConfig.weekly?.enabled}
                                onClick={() => {
                                  const nextDays = reportConfig.weekly?.daysOfWeek || [];
                                  const hasDay = nextDays.includes(day.id);
                                  const updatedDays = hasDay
                                    ? nextDays.filter((d) => d !== day.id)
                                    : [...nextDays, day.id];
                                  setValues({
                                    ...values,
                                    autoReportMailConfig: {
                                      ...reportConfig,
                                      weekly: { ...reportConfig.weekly, daysOfWeek: updatedDays },
                                    },
                                  });
                                }}
                                className={`px-2 py-1 text-[11px] font-medium rounded-md border transition-colors ${
                                  isChecked
                                    ? "bg-green-500 text-white border-green-500"
                                    : "bg-white text-gray-600 border-gray-300 hover:border-green-400"
                                } disabled:opacity-50`}
                              >
                                {day.label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">Gün seçmezseniz haftada 1 kez gönderilir.</p>
                      </div>
                    )}

                    {key === "monthly" && (
                      <div>
                        <span className="text-xs text-gray-500">Ayın Kaçında?</span>
                        <input
                          type="number"
                          min={1}
                          max={31}
                          value={reportConfig.monthly?.dayOfMonth || 1}
                          onChange={(e) => {
                            const value = Number(e.target.value) || 1;
                            const clamped = Math.min(Math.max(value, 1), 31);
                            setValues({
                              ...values,
                              autoReportMailConfig: {
                                ...reportConfig,
                                monthly: { ...reportConfig.monthly, dayOfMonth: clamped },
                              },
                            });
                          }}
                          disabled={!reportConfig.monthly?.enabled}
                          className="w-full border rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-green-500 focus:outline-none disabled:bg-gray-100 mt-0.5"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </FormCard>
        )}
      </div>

      {/* Footer */}
      <div className="flex-none pt-4 mt-4 border-t border-gray-200">
        {wizard ? (
          <div className="flex gap-3">
            {(step > 1 || onCancel) && (
              <button
                type="button"
                onClick={step === 1 ? onCancel : goBack}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
              >
                {step === 1 ? (
                  "İptal"
                ) : (
                  <>
                    <FaArrowLeft className="w-3.5 h-3.5" />
                    <span>Geri</span>
                  </>
                )}
              </button>
            )}
            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={goNext}
                disabled={!stepIsValid(step)}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-sm hover:from-blue-700 hover:to-indigo-700 hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
              >
                <span>İleri</span>
                <FaArrowRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!stepIsValid(TOTAL_STEPS)}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-sm hover:from-blue-700 hover:to-indigo-700 hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaSave className="w-4 h-4" />
                <span>Önizle</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-3">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
              >
                İptal
              </button>
            )}
            <button
              type="submit"
              className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm shadow-sm hover:from-blue-700 hover:to-indigo-700 hover:shadow transition-all duration-200 ${onCancel ? "flex-1" : "ml-auto"}`}
            >
              <FaSave className="w-4 h-4" />
              <span>Kaydet</span>
            </button>
          </div>
        )}
      </div>
    </form>
  );
}
